import { useEffect, useRef } from 'react';
import { useAudioPlayer } from 'react-use-audio-player';
import type { MatchSummary } from '../utils/matchEventsFeed';
// Distinct cues so the operator can tell a goal from a corner by ear.
import goalSound from 'src/Sounds/goal.mp3';
import cornerSound from 'src/Sounds/UI_Quirky7.mp3';

/** Combined match total (home + away) of a metric, or null when unavailable. */
function totalOf(
  metric: { total: { home: number; away: number } } | null,
): number | null {
  if (!metric) return null;
  const sum = metric.total.home + metric.total.away;
  return Number.isFinite(sum) ? sum : null;
}

/**
 * Plays a cue when the goals or corners tally INCREASES — a real new goal /
 * corner, not a confidence re-emit (the summary already collapses to the
 * authoritative reading). Goals and corners get separate audio players + clips
 * so a simultaneous pair doesn't clobber. The first observed value only seeds
 * the baseline (no cue on tab open), and a decrease (VAR revert) is silent.
 *
 * Baselines reset when `fixtureId` changes so switching matches never fires a
 * spurious cue off the new match's opening totals.
 */
export function useMatchEventSound(
  summary: MatchSummary,
  opts: { enabled: boolean; fixtureId: number | null; ready?: boolean },
): void {
  const { load: loadGoal } = useAudioPlayer();
  const { load: loadCorner } = useAudioPlayer();

  const prevGoals = useRef<number | null>(null);
  const prevCorners = useRef<number | null>(null);
  const prevFixture = useRef<number | null>(null);

  useEffect(() => {
    // New fixture → drop baselines so the first reading only seeds them.
    if (opts.fixtureId !== prevFixture.current) {
      prevFixture.current = opts.fixtureId;
      prevGoals.current = null;
      prevCorners.current = null;
    }

    // Not ready (history still loading) → don't touch baselines or play, so the
    // history load bumping the total from 0 to N never fires a spurious goal
    // cue. The FIRST summary once ready only seeds the baselines.
    if (opts.ready === false) {
      prevGoals.current = null;
      prevCorners.current = null;
      return;
    }

    const goals = totalOf(summary.goals);
    if (goals != null) {
      if (
        opts.enabled &&
        prevGoals.current != null &&
        goals > prevGoals.current
      ) {
        try {
          loadGoal(goalSound, { autoplay: true, loop: false });
        } catch {
          /* audio unavailable — the row still renders */
        }
      }
      prevGoals.current = goals;
    }

    const corners = totalOf(summary.corners);
    if (corners != null) {
      if (
        opts.enabled &&
        prevCorners.current != null &&
        corners > prevCorners.current
      ) {
        try {
          loadCorner(cornerSound, { autoplay: true, loop: false });
        } catch {
          /* audio unavailable */
        }
      }
      prevCorners.current = corners;
    }
  }, [summary, opts.enabled, opts.fixtureId, opts.ready, loadGoal, loadCorner]);
}
